import moment from "moment-timezone";

/*
 * Zoom.
 *
 * The zoom is the span of time on screen, in ms. It is remembered per browser
 * (for every node: how far someone likes to look ahead is theirs, not the
 * node's), so the matrix reopens at the span it was left at, around now.
 * Storage can be blocked inside an iframe, which just means the matrix opens
 * on the default span again.
 */
export const ZOOM_STORAGE_KEY = "matrix.zoom";

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

// The timeline's minZoom and maxZoom.
export const MIN_ZOOM_MS = 3 * HOUR_MS;
export const MAX_ZOOM_MS = 60 * DAY_MS;

// A span within the timeline's limits, or null for anything that is not one.
export const clampZoom = (span) => {
  const ms = typeof span === "string" ? Number(span) : span;
  if (typeof ms !== "number" || !Number.isFinite(ms) || ms <= 0) return null;
  return Math.min(MAX_ZOOM_MS, Math.max(MIN_ZOOM_MS, Math.round(ms)));
};

export const readZoom = () => {
  try {
    const stored = window.localStorage.getItem(ZOOM_STORAGE_KEY);
    return stored === null ? null : clampZoom(stored);
  } catch {
    return null;
  }
};

export const storeZoom = (span) => {
  const ms = clampZoom(span);
  if (ms === null) return;
  try {
    window.localStorage.setItem(ZOOM_STORAGE_KEY, String(ms));
  } catch {
    // Not persisted; the zoom still works for this visit.
  }
};

// The view to open on: `span` centred on `now`, or without a remembered span
// the default of ten days either side of today.
export const initialVisibleRange = (span, now = Date.now()) => {
  const ms = clampZoom(span);
  if (ms === null) {
    const today = moment(now).startOf("day");
    return {
      start: today.clone().add(-10, "day").valueOf(),
      end: today.clone().add(10, "day").valueOf(),
    };
  }
  return { start: now - ms / 2, end: now + ms / 2 };
};

// The same span, centred on a time: where the "today" button takes the view,
// keeping the zoom.
export const centredOn = ({ start, end }, time) => {
  const span = end - start;
  return { start: time - span / 2, end: time + span / 2 };
};
